// The detail panel's tabs (DECISIONS 2026-10-05 "detail panel tabs"): the
// header (name, summary, toggles, state) stays on top and the rest is split
// by kind, each kind with only the tabs it has. The tab chosen last is
// remembered per kind, in this browser.

export type TabId =
    'live' | 'caster' | 'audio' | 'graphs' | 'clients' | 'events' | 'overview' | 'network' | 'health' | 'monitoring'
export type Tab = { id: TabId; label: string }

export type TabKind = 'input' | 'encode' | 'destination' | 'output' | 'machine' | 'storage'

// what decides an entity's tabs beyond its kind; health: the machine can be
// checked before the event and has problems to acknowledge (a node, not a
// saved config); monitoring: it can be watched (an encoder node, not a
// player or a saved config)
export type TabFacts = { caster?: boolean; pull?: boolean; health?: boolean; monitoring?: boolean }

const t = (id: TabId, label: string): Tab => ({ id, label })

export function tabsFor(kind: TabKind, f: TabFacts = {}): Tab[] {
    switch (kind) {
        case 'input':
            return [
                t('live', 'Live'),
                ...(f.caster ? [t('caster', 'Caster')] : []),
                t('audio', 'Audio'),
                t('graphs', 'Graphs'),
                t('events', 'Events'),
            ]
        case 'destination':
            return [
                t('live', 'Live'),
                ...(f.pull ? [t('clients', 'Clients')] : []),
                t('graphs', 'Graphs'),
                t('events', 'Events'),
            ]
        case 'machine':
            return [
                t('overview', 'Overview'),
                ...(f.health ? [t('health', 'Health')] : []),
                t('graphs', 'Graphs'),
                t('network', 'Network'), // its interfaces, then the clock and its time servers
                ...(f.monitoring ? [t('monitoring', 'Monitoring')] : []),
            ]
        case 'storage':
            return [] // a few lines: no tabs
        case 'encode':
        case 'output':
            return [t('live', 'Live'), t('graphs', 'Graphs'), t('events', 'Events')]
    }
}

// pickTab is the remembered tab when this entity has it, else its first
export function pickTab(tabs: Tab[], remembered: string | null): TabId | '' {
    return tabs.find((x) => x.id === remembered)?.id ?? tabs[0]?.id ?? ''
}

const key = (kind: TabKind) => `ese.detail.tab.${kind}`

export function rememberedTab(kind: TabKind): string | null {
    try {
        return localStorage.getItem(key(kind))
    } catch {
        return null // storage blocked
    }
}

export function rememberTab(kind: TabKind, id: TabId) {
    try {
        localStorage.setItem(key(kind), id)
    } catch {
        /* storage blocked: the first tab next time */
    }
}
